import { useLoader } from '@react-three/fiber';
import { omit } from 'lodash';
import React, { ReactNode, useContext, useEffect, useRef } from 'react';
import Node from './Node';
import {
  AssetModel,
  AssetState,
  ConfigurationType,
  OnAttributes,
} from '../types';
import { useAssetInternal } from './hooks/useAsset';
import AssetModelLoader from './AssetModelLoader';
import ThreekitSourceContext from './ThreekitSourceContext';

interface AssetProps {
  id?: string; // | AssetFindOptions; // required one of `id` or `assetState`
  refkey?: string;
  configuration?: ConfigurationType;
  overrides?: any;
  fromId?: string; // This is an internal prop, used to distinguish between an internal, nested asset and an externally requested asset
  withStage?: Boolean | string;
  children?: ReactNode; //  React.PropTypes.func.isRequired;
}

function usePrevious(value: any) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value; //assign the value of ref to the argument
  }, [value]); //this code will run when the value of 'value' changes
  return ref.current; //in the end, return the current ref value.
}

export function CustomNode(props: any) {
  // console.log('run custom node with', props);
  const { id, attachHandlers } = props;
  const rest = omit(props, 'id', 'attachHandlers');
  attachHandlers(id, rest);
  return null;
}

export default function Asset(props: AssetProps) {
  const { withStage } = props;

  const { appliedAssetState, appliedConfiguration } = useAssetInternal(props);

  // Add our asset to the registry.
  // useEffect(() => {
  //   const parentId = props.assetState?.id;
  //   threekitApi.addAsset(
  //     { source, assetState: assetState, configuration },
  //     parentId === id ? undefined : parentId
  //   );
  // }, [id]);

  if (withStage) {
    const { source } = useContext(ThreekitSourceContext);
    const asset = useLoader<AssetModel, any>(
      AssetModelLoader,
      appliedAssetState.id,
      AssetModelLoader.attach(source)
    );
    // Check if we want ot render a stage
    const stageId =
      typeof withStage === 'string'
        ? withStage
        : (Array.isArray(asset) ? asset[0] : asset).defaultStageId;

    if (stageId) {
      return (
        <Asset
          id={stageId}
          key={stageId}
          configuration={{
            Asset: {
              assetId: appliedAssetState.id,
              configuration: appliedConfiguration,
            },
          }}
        />
      );
    }
  }

  const childProps: { [id: string]: any } = {};

  const attachHandlers = (id: string, props: any) => {
    // console.log('attach?', props, 'to', id);
    childProps[id] = props;
  };

  const childrenWithProps = React.Children.map(props.children, (child) => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child)) {
      // @ts-ignore
      return React.cloneElement(child, { attachHandlers });
    }
    return child;
  });

  return (
    <>
      {childrenWithProps}
      <Node
        {...omit(props, 'withStage')}
        node={appliedAssetState.nodes[appliedAssetState.id]}
        assetState={appliedAssetState}
        configuration={appliedConfiguration}
        childProps={childProps}
      />
    </>
  );
}
